// ═══════════════════════════════════════════════════════════════
// RECONCILIATION REPORT
// Path: src/components/reports/ReconciliationReport.jsx
//
// Compare CRM gift data against an uploaded CSV (e.g. Spektrix
// export or Excel export). Shows fund-level and row-level
// discrepancies to help identify missing, extra, or mismatched
// transactions.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useCallback } from "react";
import {
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle,
  Download,
  Filter,
  Search,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { supabase } from "../../lib/supabase";
import { SpektrixImportModal } from "../features/SpektrixImportModal";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount || 0);

// ── Parse CSV text ──────────────────────────────────────────
function parseLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === "," || char === "\t") && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function parseCSV(text) {
  const clean = text.replace(/^\uFEFF/, "");
  const lines = clean.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = parseLine(lines[0]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseLine(lines[i]);
    if (values.length === 0) continue;
    const row = {};
    headers.forEach((h, idx) => {
      row[h.trim()] = (values[idx] || "").trim();
    });
    rows.push(row);
  }
  return { headers, rows };
}

// ── Normalize date to YYYY-MM-DD ────────────────────────────
function normalizeDate(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (parts) return `${parts[3]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return null;
}

// ── Auto-detect column names ────────────────────────────────
function detectColumns(headers) {
  const lower = headers.map((h) => h.toLowerCase());
  const find = (keywords) =>
    headers[lower.findIndex((h) => keywords.some((k) => h.includes(k)))] || null;

  return {
    amount: find(["amount", "gift amount", "total"]),
    date: find(["date confirmed", "accounting date", "gift_date", "gift date", "date"]),
    fund: find(["fund name", "fund", "fund_name"]),
    donor: find(["owner name", "facilitator name", "donor", "name"]),
  };
}

export const ReconciliationReport = () => {
  const { palette, showMessage } = useApp();

  // File state
  const [csvContent, setCsvContent] = useState(null);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvRows, setCsvRows] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columns, setColumns] = useState({ amount: null, date: null, fund: null, donor: null });

  // CRM data state
  const [crmGifts, setCrmGifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compared, setCompared] = useState(false);

  // Results
  const [results, setResults] = useState(null);
  const [detailFilter, setDetailFilter] = useState("all"); // all, crm_only, csv_only, matched

  // Import Missing modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCsvContent, setImportCsvContent] = useState(null);

  // ── File upload ─────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      setCsvContent(text);
      const { headers, rows } = parseCSV(text);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setColumns(detectColumns(headers));
      setCompared(false);
      setResults(null);
    };
    reader.readAsText(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      setCsvContent(text);
      const { headers, rows } = parseCSV(text);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setColumns(detectColumns(headers));
      setCompared(false);
      setResults(null);
    };
    reader.readAsText(file);
  }, []);

  // ── Run comparison ──────────────────────────────────────────
  const handleCompare = async () => {
    if (!csvRows.length || !columns.amount || !columns.date) return;
    setLoading(true);

    try {
      // Get date range from CSV
      const csvDates = csvRows
        .map((r) => normalizeDate(r[columns.date]))
        .filter(Boolean)
        .sort();

      if (csvDates.length === 0) {
        showMessage("Could not parse any dates from the CSV. Check the date column format.", "error");
        setLoading(false);
        return;
      }

      const minDate = csvDates[0];
      const maxDate = csvDates[csvDates.length - 1];

      // Fetch CRM gifts for that date range (cursor-paginated by id)
      let allGifts = [];
      let lastId = null;
      const pageSize = 1000;
      let pages = 0;
      while (pages < 50) { // safety limit: max 50,000 gifts
        let query = supabase
          .from("gifts")
          .select("id, amount, gift_date, payment_method, fund_id, member_id, funds (id, name, code), members:member_id (first_name, last_name, spektrix_id)")
          .gte("gift_date", minDate)
          .lte("gift_date", maxDate)
          .order("id", { ascending: true })
          .limit(pageSize);

        if (lastId) query = query.gt("id", lastId);

        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allGifts = allGifts.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
        pages++;
      }

      setCrmGifts(allGifts);

      // Build comparison
      const comparison = buildComparison(csvRows, allGifts, columns);
      setResults(comparison);
      setCompared(true);
    } catch (err) {
      showMessage("Reconciliation failed: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Build comparison logic ──────────────────────────────────
  const buildComparison = (csv, crm, cols) => {
    // Fund-level summaries
    const csvByFund = {};
    const csvDetails = [];

    csv.forEach((row, idx) => {
      const amount = parseFloat((row[cols.amount] || "").replace(/[^0-9.-]/g, "")) || 0;
      const date = normalizeDate(row[cols.date]);
      const fund = cols.fund ? (row[cols.fund] || "Undesignated") : "Undesignated";
      const donor = cols.donor ? (row[cols.donor] || "Unknown") : "Unknown";

      if (amount <= 0 || !date) return;

      if (!csvByFund[fund]) csvByFund[fund] = { count: 0, total: 0 };
      csvByFund[fund].count++;
      csvByFund[fund].total += amount;

      csvDetails.push({ source: "csv", idx, amount, date, fund, donor, key: `${date}|${amount}` });
    });

    const crmByFund = {};
    const crmDetails = [];

    crm.forEach((gift) => {
      const amount = parseFloat(gift.amount) || 0;
      const date = gift.gift_date;
      const fund = gift.funds?.name || "Undesignated";
      const donor = gift.members
        ? `${gift.members.first_name || ""} ${gift.members.last_name || ""}`.trim()
        : "Unknown";

      if (!crmByFund[fund]) crmByFund[fund] = { count: 0, total: 0 };
      crmByFund[fund].count++;
      crmByFund[fund].total += amount;

      crmDetails.push({ source: "crm", id: gift.id, amount, date, fund, donor, key: `${date}|${amount}` });
    });

    // Fund-level comparison
    const allFunds = new Set([...Object.keys(csvByFund), ...Object.keys(crmByFund)]);
    const fundComparison = [];
    allFunds.forEach((fund) => {
      const csv = csvByFund[fund] || { count: 0, total: 0 };
      const crm = crmByFund[fund] || { count: 0, total: 0 };
      fundComparison.push({
        fund,
        csvCount: csv.count,
        csvTotal: csv.total,
        crmCount: crm.count,
        crmTotal: crm.total,
        countDiff: crm.count - csv.count,
        totalDiff: crm.total - csv.total,
        match: Math.abs(crm.total - csv.total) < 0.01 && crm.count === csv.count,
      });
    });
    fundComparison.sort((a, b) => Math.abs(b.totalDiff) - Math.abs(a.totalDiff));

    // Row-level matching — two-pass strategy:
    //   Pass 1: exact match on date + amount + fund (case-insensitive)
    //   Pass 2: match on date + amount only (flags fund mismatch)
    const crmUnmatched = [...crmDetails];
    const csvUnmatched = [];
    const matched = [];
    const fundMismatches = [];

    // Pass 1: exact match (case-insensitive fund)
    const pass1Remaining = [];
    csvDetails.forEach((csvRow) => {
      const csvFundLower = (csvRow.fund || "").toLowerCase().trim();
      const matchIdx = crmUnmatched.findIndex(
        (c) =>
          c.date === csvRow.date &&
          Math.abs(c.amount - csvRow.amount) < 0.01 &&
          (c.fund || "").toLowerCase().trim() === csvFundLower
      );
      if (matchIdx >= 0) {
        matched.push({ csv: csvRow, crm: crmUnmatched[matchIdx] });
        crmUnmatched.splice(matchIdx, 1);
      } else {
        pass1Remaining.push(csvRow);
      }
    });

    // Pass 2: match on date + amount only (fund mismatch)
    pass1Remaining.forEach((csvRow) => {
      const matchIdx = crmUnmatched.findIndex(
        (c) =>
          c.date === csvRow.date &&
          Math.abs(c.amount - csvRow.amount) < 0.01
      );
      if (matchIdx >= 0) {
        const crmRow = crmUnmatched[matchIdx];
        matched.push({ csv: csvRow, crm: crmRow });
        fundMismatches.push({
          csv: csvRow,
          crm: crmRow,
          csvFund: csvRow.fund,
          crmFund: crmRow.fund,
        });
        crmUnmatched.splice(matchIdx, 1);
      } else {
        csvUnmatched.push(csvRow);
      }
    });

    return {
      fundComparison,
      matched,
      csvOnly: csvUnmatched,
      crmOnly: crmUnmatched,
      fundMismatches,
      csvTotal: csvDetails.reduce((s, r) => s + r.amount, 0),
      crmTotal: crmDetails.reduce((s, r) => s + r.amount, 0),
      csvCount: csvDetails.length,
      crmCount: crmDetails.length,
    };
  };

  // ── Filtered detail rows ────────────────────────────────────
  const detailRows = useMemo(() => {
    if (!results) return [];
    if (detailFilter === "csv_only") return results.csvOnly.map((r) => ({ ...r, status: "CSV Only" }));
    if (detailFilter === "crm_only") return results.crmOnly.map((r) => ({ ...r, status: "CRM Only" }));
    if (detailFilter === "matched") return results.matched.map((m) => ({ ...m.csv, status: "Matched" }));
    if (detailFilter === "fund_mismatch") return results.fundMismatches.map((m) => ({
      ...m.csv, status: "Fund Mismatch", crmFund: m.crmFund, csvFund: m.csvFund,
    }));
    return [
      ...results.csvOnly.map((r) => ({ ...r, status: "CSV Only" })),
      ...results.crmOnly.map((r) => ({ ...r, status: "CRM Only" })),
      ...results.fundMismatches.map((m) => ({ ...m.csv, status: "Fund Mismatch", crmFund: m.crmFund, csvFund: m.csvFund })),
    ].sort((a, b) => a.date.localeCompare(b.date));
  }, [results, detailFilter]);

  // ── Export discrepancies as CSV ─────────────────────────────
  const handleExport = () => {
    if (!results) return;
    const rows = [
      ...results.csvOnly.map((r) => ({ Status: "In CSV Not CRM", Donor: r.donor, Date: r.date, Amount: r.amount, Fund: r.fund })),
      ...results.crmOnly.map((r) => ({ Status: "In CRM Not CSV", Donor: r.donor, Date: r.date, Amount: r.amount, Fund: r.fund })),
    ];
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Import missing rows via SpektrixImportModal ────────────
  const handleImportMissing = () => {
    if (!results || results.csvOnly.length === 0) return;

    // Get the original CSV row indices that were unmatched
    const missingIndices = new Set(results.csvOnly.map((r) => r.idx));

    // Reconstruct a CSV with only the missing rows, using the original headers
    const headerLine = csvHeaders.map((h) => `"${h}"`).join(",");
    const dataLines = csvRows
      .filter((_, idx) => missingIndices.has(idx))
      .map((row) =>
        csvHeaders
          .map((h) => `"${(row[h] || "").replace(/"/g, '""')}"`)
          .join(",")
      );

    const reconstructedCsv = [headerLine, ...dataLines].join("\n");
    setImportCsvContent(reconstructedCsv);
    setShowImportModal(true);
  };

  const handleImportModalClose = () => {
    setShowImportModal(false);
    setImportCsvContent(null);
    // Re-run comparison to see updated results
    if (csvRows.length > 0) {
      handleCompare();
    }
  };

  // ── Styles ──────────────────────────────────────────────────
  const th = {
    padding: "6px 10px",
    textAlign: "left",
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
  };

  const td = {
    padding: "5px 10px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
  };

  return (
    <div>
      {/* ── Upload Section ─────────────────────────────────── */}
      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Search size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
          <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
            Reconciliation
          </span>
          <span style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY }}>
            — Upload your source CSV to compare against CRM data
          </span>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {/* Drop zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => document.getElementById("recon-file-input")?.click()}
            style={{
              border: `2px dashed ${csvContent ? "#16a34a" : palette.cardBorder}`,
              borderRadius: 8,
              padding: "16px 24px",
              textAlign: "center",
              background: csvContent ? "#f0fdf4" : `${palette.accent1}04`,
              cursor: "pointer",
              flex: "1 1 200px",
              transition: "all 0.2s",
            }}
          >
            <input
              id="recon-file-input"
              type="file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            {csvContent ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <FileText size={ICON_SIZES.sm} style={{ color: "#16a34a" }} />
                <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: "#16a34a", fontFamily: FONT_FAMILY }}>
                  {csvFileName} — {csvRows.length} rows
                </span>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <Upload size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                <span style={{ fontSize: FONT_SIZES.xs, color: palette.text2, fontFamily: FONT_FAMILY }}>
                  Drop CSV or click to browse
                </span>
              </div>
            )}
          </div>

          {/* Column mapping display */}
          {csvContent && (
            <div style={{ fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY, color: palette.text2 }}>
              <div>Amount: <strong style={{ color: columns.amount ? "#16a34a" : "#dc2626" }}>{columns.amount || "Not found"}</strong></div>
              <div>Date: <strong style={{ color: columns.date ? "#16a34a" : "#dc2626" }}>{columns.date || "Not found"}</strong></div>
              <div>Fund: <strong style={{ color: columns.fund ? "#16a34a" : "#f59e0b" }}>{columns.fund || "None"}</strong></div>
              <div>Donor: <strong style={{ color: columns.donor ? "#16a34a" : "#f59e0b" }}>{columns.donor || "None"}</strong></div>
            </div>
          )}

          <ActionButton
            label={loading ? "Comparing..." : "Compare"}
            icon={Search}
            onClick={handleCompare}
            disabled={!csvContent || !columns.amount || !columns.date || loading}
          />
        </div>
      </Card>

      {/* ── Results ────────────────────────────────────────── */}
      {compared && results && (
        <>
          {/* Summary tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(results.csvTotal)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                CSV Total ({results.csvCount})
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(results.crmTotal)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                CRM Total ({results.crmCount})
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <div style={{
                fontSize: FONT_SIZES.xl, fontWeight: 700, fontFamily: FONT_FAMILY,
                color: Math.abs(results.crmTotal - results.csvTotal) < 0.01 ? "#16a34a" : "#dc2626",
              }}>
                {fmtCurrency(results.crmTotal - results.csvTotal)}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Difference
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <div style={{
                fontSize: FONT_SIZES.xl, fontWeight: 700, fontFamily: FONT_FAMILY,
                color: results.csvOnly.length === 0 && results.crmOnly.length === 0 ? "#16a34a" : "#f59e0b",
              }}>
                {results.csvOnly.length + results.crmOnly.length}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Unmatched Rows
              </div>
            </Card>
          </div>

          {/* Fund mismatch warning */}
          {results.fundMismatches.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                marginBottom: 12,
                background: "#f5f3ff",
                border: "1px solid #c4b5fd",
                borderRadius: 8,
              }}
            >
              <AlertTriangle size={ICON_SIZES.sm} style={{ color: "#7c3aed", flexShrink: 0 }} />
              <div style={{ fontSize: FONT_SIZES.xs, color: "#5b21b6", fontFamily: FONT_FAMILY, lineHeight: 1.4 }}>
                <strong>{results.fundMismatches.length} gift{results.fundMismatches.length !== 1 ? "s" : ""} matched by date + amount but have different fund names</strong> between
                your CSV and the CRM. These are counted as matched (not missing), but the fund name discrepancy may affect
                fund-level totals. Use the "Fund Mismatch" filter below to review them.
              </div>
            </div>
          )}

          {/* Fund-level comparison */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
              fontFamily: FONT_FAMILY, marginBottom: 12,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span>Fund-Level Comparison</span>
              {(results.csvOnly.length > 0 || results.crmOnly.length > 0) && (
                <div style={{ display: "flex", gap: 8 }}>
                  {results.csvOnly.length > 0 && (
                    <ActionButton
                      label={`Import ${results.csvOnly.length} Missing`}
                      icon={Upload}
                      small
                      onClick={handleImportMissing}
                    />
                  )}
                  <ActionButton label="Export Discrepancies" icon={Download} small variant="secondary" onClick={handleExport} />
                </div>
              )}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
              <thead>
                <tr>
                  <th style={th}>Fund</th>
                  <th style={{ ...th, textAlign: "right" }}>CSV Count</th>
                  <th style={{ ...th, textAlign: "right" }}>CSV Total</th>
                  <th style={{ ...th, textAlign: "right" }}>CRM Count</th>
                  <th style={{ ...th, textAlign: "right" }}>CRM Total</th>
                  <th style={{ ...th, textAlign: "right" }}>Count Diff</th>
                  <th style={{ ...th, textAlign: "right" }}>Amount Diff</th>
                  <th style={{ ...th, textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {results.fundComparison.map((f) => (
                  <tr key={f.fund}>
                    <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>{f.fund}</td>
                    <td style={{ ...td, textAlign: "right", color: palette.text2 }}>{f.csvCount}</td>
                    <td style={{ ...td, textAlign: "right", color: palette.text2 }}>{fmtCurrency(f.csvTotal)}</td>
                    <td style={{ ...td, textAlign: "right", color: palette.text2 }}>{f.crmCount}</td>
                    <td style={{ ...td, textAlign: "right", color: palette.text2 }}>{fmtCurrency(f.crmTotal)}</td>
                    <td style={{
                      ...td, textAlign: "right", fontWeight: 600,
                      color: f.countDiff === 0 ? "#16a34a" : "#dc2626",
                    }}>
                      {f.countDiff > 0 ? "+" : ""}{f.countDiff}
                    </td>
                    <td style={{
                      ...td, textAlign: "right", fontWeight: 600,
                      color: Math.abs(f.totalDiff) < 0.01 ? "#16a34a" : "#dc2626",
                    }}>
                      {f.totalDiff > 0 ? "+" : ""}{fmtCurrency(f.totalDiff)}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {f.match ? (
                        <CheckCircle size={ICON_SIZES.sm} style={{ color: "#16a34a" }} />
                      ) : (
                        <AlertTriangle size={ICON_SIZES.sm} style={{ color: "#f59e0b" }} />
                      )}
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr style={{ borderTop: `2px solid ${palette.cardBorder}` }}>
                  <td style={{ ...td, fontWeight: 700, color: palette.text1 }}>Total</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{results.csvCount}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmtCurrency(results.csvTotal)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{results.crmCount}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmtCurrency(results.crmTotal)}</td>
                  <td style={{
                    ...td, textAlign: "right", fontWeight: 700,
                    color: results.crmCount - results.csvCount === 0 ? "#16a34a" : "#dc2626",
                  }}>
                    {results.crmCount - results.csvCount > 0 ? "+" : ""}{results.crmCount - results.csvCount}
                  </td>
                  <td style={{
                    ...td, textAlign: "right", fontWeight: 700,
                    color: Math.abs(results.crmTotal - results.csvTotal) < 0.01 ? "#16a34a" : "#dc2626",
                  }}>
                    {results.crmTotal - results.csvTotal > 0 ? "+" : ""}{fmtCurrency(results.crmTotal - results.csvTotal)}
                  </td>
                  <td style={td} />
                </tr>
              </tbody>
            </table>
          </Card>

          {/* Row-level detail */}
          <Card>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 12, flexWrap: "wrap", gap: 8,
            }}>
              <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                Transaction Detail
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {[
                  { id: "all", label: "All Discrepancies" },
                  { id: "csv_only", label: `CSV Only (${results.csvOnly.length})`, color: "#3b82f6" },
                  { id: "crm_only", label: `CRM Only (${results.crmOnly.length})`, color: "#f59e0b" },
                  ...(results.fundMismatches.length > 0
                    ? [{ id: "fund_mismatch", label: `Fund Mismatch (${results.fundMismatches.length})`, color: "#8b5cf6" }]
                    : []),
                  { id: "matched", label: `Matched (${results.matched.length})`, color: "#16a34a" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setDetailFilter(f.id)}
                    style={{
                      padding: "3px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
                      fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
                      background: detailFilter === f.id ? (f.color || palette.accent1) : `${palette.text3}10`,
                      color: detailFilter === f.id ? "#fff" : palette.text2,
                      transition: "all 0.15s",
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {detailRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: palette.text3, fontSize: FONT_SIZES.xs }}>
                {detailFilter === "csv_only" || detailFilter === "crm_only"
                  ? "No discrepancies found — all transactions matched!"
                  : "No transactions to display."}
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                  <thead>
                    <tr style={{ position: "sticky", top: 0, background: palette.cardBg }}>
                      <th style={th}>Status</th>
                      <th style={th}>Donor</th>
                      <th style={th}>Date</th>
                      <th style={{ ...th, textAlign: "right" }}>Amount</th>
                      <th style={th}>Fund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.slice(0, 500).map((row, idx) => (
                      <tr key={idx}>
                        <td style={td}>
                          <span style={{
                            padding: "2px 6px", borderRadius: 6, fontSize: "0.6rem", fontWeight: 600,
                            background: row.status === "Matched" ? "#dcfce7"
                              : row.status === "CSV Only" ? "#dbeafe"
                              : row.status === "Fund Mismatch" ? "#ede9fe"
                              : "#fef3c7",
                            color: row.status === "Matched" ? "#166534"
                              : row.status === "CSV Only" ? "#1e40af"
                              : row.status === "Fund Mismatch" ? "#6d28d9"
                              : "#92400e",
                          }}>
                            {row.status}
                          </span>
                        </td>
                        <td style={{ ...td, color: palette.text1 }}>{row.donor}</td>
                        <td style={{ ...td, color: palette.text2 }}>{row.date}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 600, color: palette.text1 }}>
                          {fmtCurrency(row.amount)}
                        </td>
                        <td style={{ ...td, color: row.status === "Fund Mismatch" ? "#8b5cf6" : palette.text2 }}>
                          {row.status === "Fund Mismatch"
                            ? <span>CSV: {row.csvFund} → CRM: {row.crmFund}</span>
                            : row.fund}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {detailRows.length > 500 && (
                  <div style={{ textAlign: "center", padding: 8, fontSize: FONT_SIZES.xs, color: palette.text3 }}>
                    Showing first 500 of {detailRows.length} rows
                  </div>
                )}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Import Missing modal */}
      {showImportModal && importCsvContent && (
        <SpektrixImportModal
          onClose={handleImportModalClose}
          preloadedContent={importCsvContent}
          preloadedFileName={`missing-${results?.csvOnly.length || 0}-gifts.csv`}
        />
      )}
    </div>
  );
};
